import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Table, Badge, Form, Alert, Spinner, Row, Col, InputGroup } from 'react-bootstrap';
import { workoutAPI } from '../services/workoutAPI';
import '../styles/WorkoutTracker.scss';

const WorkoutHistory = ({ onEditWorkout }) => {
  const [workoutLogs, setWorkoutLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState({
    show: false,
    workoutId: null,
    workoutName: ''
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [filters, setFilters] = useState({
    limit: 100,
    searchTerm: '',
    programFilter: 'all',
    sourceFilter: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'workout_date',
    direction: 'desc'
  });
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    fetchWorkouts();
    fetchPrograms();
  }, []);

  useEffect(() => {
    fetchWorkouts();
  }, [filters, sortConfig]);

  const fetchPrograms = async () => {
    try {
      const allPrograms = await workoutAPI.fetchPrograms();
      setPrograms(allPrograms);
    } catch (err) {
      console.error('Error fetching programs:', err);
    }
  };

  const fetchWorkouts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const logs = await workoutAPI.fetchWorkoutLogs({ limit: filters.limit });
      setWorkoutLogs(logs);
    } catch (err) {
      console.error('Error fetching workout logs:', err);
      setError('Failed to load workout history. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getFilteredAndSortedWorkouts = () => {
    let filtered = [...workoutLogs];

    // Apply search filter
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(w =>
        (w.name && w.name.toLowerCase().includes(term)) ||
        (w.program_name && w.program_name.toLowerCase().includes(term))
      );
    }

    // Apply program filter
    if (filters.programFilter !== 'all') {
      if (filters.programFilter === 'none') {
        filtered = filtered.filter(w => !w.program_id);
      } else {
        filtered = filtered.filter(w => w.program_id === parseInt(filters.programFilter));
      }
    }

    // Apply source filter
    if (filters.sourceFilter === 'program') {
      filtered = filtered.filter(w => w.program_workout_id);
    } else if (filters.sourceFilter === 'template') {
      filtered = filtered.filter(w => w.template_id && !w.program_workout_id);
    } else if (filters.sourceFilter === 'manual') {
      filtered = filtered.filter(w => !w.template_id && !w.program_workout_id);
    }

    // Apply date range filter
    if (filters.dateFrom) {
      filtered = filtered.filter(w => new Date(w.workout_date) >= new Date(filters.dateFrom));
    }
    if (filters.dateTo) {
      filtered = filtered.filter(w => new Date(w.workout_date) <= new Date(filters.dateTo));
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      // Special handling for dates
      if (sortConfig.key === 'workout_date') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      // Special handling for numbers
      if (sortConfig.key === 'duration_minutes' || sortConfig.key === 'exercise_count') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }

      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      limit: 100,
      searchTerm: '',
      programFilter: 'all',
      sourceFilter: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleViewDetails = async (workoutLog) => {
    try {
      setError(null);
      const fullWorkout = await workoutAPI.fetchWorkoutLogById(workoutLog.id);
      setSelectedWorkout(fullWorkout);
      setShowViewModal(true);
    } catch (err) {
      console.error('Error fetching workout details:', err);
      setError('Failed to load workout details.');
    }
  };

  const handleEdit = async (workoutLog) => {
    try {
      setError(null);
      const fullWorkout = await workoutAPI.fetchWorkoutLogById(workoutLog.id);
      onEditWorkout(fullWorkout);
    } catch (err) {
      console.error('Error fetching workout for edit:', err);
      setError('Failed to load workout for editing.');
    }
  };

  const handleDeleteConfirm = (workoutLog) => {
    setShowDeleteConfirm({
      show: true,
      workoutId: workoutLog.id,
      workoutName: workoutLog.name
    });
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm.workoutId) return;

    try {
      setIsDeleting(true);
      setError(null);
      await workoutAPI.deleteWorkoutLog(showDeleteConfirm.workoutId);
      setShowDeleteConfirm({ show: false, workoutId: null, workoutName: '' });
      fetchWorkouts(); // Refresh the list
    } catch (err) {
      console.error('Error deleting workout:', err);
      setError('Failed to delete workout. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5); // HH:MM
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const totalSeconds = Math.round(minutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDurationFromSeconds = (seconds) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const renderExerciseDetails = (exercise) => {
    if (exercise.item_type === 'rest') {
      return (
        <tr key={exercise.id}>
          <td colSpan="4">
            <Badge bg="secondary">REST</Badge>
            {' '}{formatDurationFromSeconds(exercise.rest_duration_seconds)}
            {exercise.rest_notes && ` - ${exercise.rest_notes}`}
          </td>
        </tr>
      );
    }

    if (exercise.exercise_type === 'weight_reps' || exercise.exercise_type === 'weight_time') {
      return (
        <React.Fragment key={exercise.id}>
          <tr>
            <td colSpan="4">
              <strong>{exercise.name}</strong>
              {exercise.notes && <div className="text-muted small">{exercise.notes}</div>}
            </td>
          </tr>
          {exercise.sets && exercise.sets.map((set) => (
            <tr key={set.id}>
              <td className="ps-4">Set {set.set_number}</td>
              <td>
                {set.weight} {set.weight_unit}
              </td>
              <td>
                {exercise.exercise_type === 'weight_reps'
                  ? `${set.reps} reps`
                  : `${set.duration_seconds}s`}
              </td>
              <td>{set.notes}</td>
            </tr>
          ))}
        </React.Fragment>
      );
    }

    if (exercise.exercise_type === 'cardio_basic' && exercise.cardio) {
      return (
        <React.Fragment key={exercise.id}>
          <tr>
            <td colSpan="4">
              <strong>{exercise.name}</strong>
              {exercise.notes && <div className="text-muted small">{exercise.notes}</div>}
            </td>
          </tr>
          <tr>
            <td className="ps-4" colSpan="2">
              {exercise.cardio.distance} {exercise.cardio.distance_unit}
            </td>
            <td colSpan="2">
              {formatDurationFromSeconds(exercise.cardio.duration_seconds)}
              {exercise.cardio.heart_rate_avg && ` | Avg HR: ${exercise.cardio.heart_rate_avg}`}
              {exercise.cardio.heart_rate_max && ` | Max HR: ${exercise.cardio.heart_rate_max}`}
            </td>
          </tr>
        </React.Fragment>
      );
    }

    if (exercise.exercise_type === 'cardio_segments' && exercise.segments) {
      return (
        <React.Fragment key={exercise.id}>
          <tr>
            <td colSpan="4">
              <strong>{exercise.name}</strong>
              {exercise.notes && <div className="text-muted small">{exercise.notes}</div>}
            </td>
          </tr>
          {exercise.segments.map((seg) => {
            return (
              <tr key={seg.id}>
                <td className="ps-4">Segment {seg.segment_number}</td>
                <td>
                  {seg.distance} {seg.distance_unit}
                </td>
                <td>
                  {formatDurationFromSeconds(seg.duration_seconds)}
                </td>
                <td>
                  {seg.heart_rate_avg && `Avg: ${seg.heart_rate_avg}`}
                  {seg.heart_rate_max && ` | Max: ${seg.heart_rate_max}`}
                </td>
              </tr>
            );
          })}
        </React.Fragment>
      );
    }

    return null;
  };

  const filteredWorkouts = getFilteredAndSortedWorkouts();

  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fas fa-sort text-muted ms-1"></i>;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fas fa-sort-up ms-1"></i>
      : <i className="fas fa-sort-down ms-1"></i>;
  };

  const hasActiveFilters = () => {
    return filters.searchTerm ||
           filters.programFilter !== 'all' ||
           filters.sourceFilter !== 'all' ||
           filters.dateFrom ||
           filters.dateTo;
  };

  if (isLoading && workoutLogs.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading workout history...</p>
      </div>
    );
  }

  return (
    <div className="workout-history">
      <h3 className="mb-4">Workout History</h3>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters Section */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col md={6} lg={3}>
              <Form.Label className="small mb-1">Search</Form.Label>
              <InputGroup size="sm">
                <InputGroup.Text>
                  <i className="fas fa-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search workouts..."
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                />
              </InputGroup>
            </Col>

            <Col md={6} lg={2}>
              <Form.Label className="small mb-1">Source</Form.Label>
              <Form.Select
                size="sm"
                value={filters.sourceFilter}
                onChange={(e) => handleFilterChange('sourceFilter', e.target.value)}
              >
                <option value="all">All Sources</option>
                <option value="program">From Program</option>
                <option value="template">From Template</option>
                <option value="manual">Manual Entry</option>
              </Form.Select>
            </Col>

            <Col md={6} lg={2}>
              <Form.Label className="small mb-1">Program</Form.Label>
              <Form.Select
                size="sm"
                value={filters.programFilter}
                onChange={(e) => handleFilterChange('programFilter', e.target.value)}
              >
                <option value="all">All Programs</option>
                <option value="none">No Program</option>
                {programs.map(prog => (
                  <option key={prog.id} value={prog.id}>{prog.name}</option>
                ))}
              </Form.Select>
            </Col>

            <Col md={6} lg={2}>
              <Form.Label className="small mb-1">Date From</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </Col>

            <Col md={6} lg={2}>
              <Form.Label className="small mb-1">Date To</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </Col>

            <Col md={6} lg={1} className="d-flex align-items-end">
              {hasActiveFilters() && (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={clearFilters}
                  className="w-100"
                >
                  Clear
                </Button>
              )}
            </Col>
          </Row>

          <div className="mt-2 small text-muted">
            Showing {filteredWorkouts.length} of {workoutLogs.length} workouts
          </div>
        </Card.Body>
      </Card>

      {workoutLogs.length === 0 ? (
        <Card className="text-center p-5">
          <Card.Body>
            <i className="fas fa-history fa-3x text-muted mb-3"></i>
            <p className="text-muted">No workout history yet. Start logging workouts to see them here!</p>
          </Card.Body>
        </Card>
      ) : filteredWorkouts.length === 0 ? (
        <Card className="text-center p-5">
          <Card.Body>
            <i className="fas fa-filter fa-3x text-muted mb-3"></i>
            <p className="text-muted">No workouts match your current filters.</p>
            <Button size="sm" variant="outline-primary" onClick={clearFilters}>
              Clear Filters
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort('workout_date')}
                    style={{ cursor: 'pointer' }}
                  >
                    Date {renderSortIcon('workout_date')}
                  </th>
                  <th
                    onClick={() => handleSort('name')}
                    style={{ cursor: 'pointer' }}
                  >
                    Workout {renderSortIcon('name')}
                  </th>
                  <th>Program/Source</th>
                  <th
                    onClick={() => handleSort('duration_minutes')}
                    style={{ cursor: 'pointer' }}
                  >
                    Duration {renderSortIcon('duration_minutes')}
                  </th>
                  <th
                    onClick={() => handleSort('exercise_count')}
                    style={{ cursor: 'pointer' }}
                    className="text-center"
                  >
                    Exercises {renderSortIcon('exercise_count')}
                  </th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredWorkouts.map((workout) => (
                  <tr key={workout.id}>
                    <td className="text-nowrap">
                      <div>{formatDate(workout.workout_date)}</div>
                      {workout.workout_time && (
                        <div className="text-muted small">
                          {formatTime(workout.workout_time)}
                        </div>
                      )}
                    </td>
                    <td>
                      <strong>{workout.name || 'Untitled Workout'}</strong>
                    </td>
                    <td>
                      {workout.program_workout_id && workout.program_name && (
                        <div>
                          <Badge bg="success" className="me-1">
                            <i className="fas fa-calendar-alt me-1"></i>
                            Program
                          </Badge>
                          <div className="small">{workout.program_name}</div>
                        </div>
                      )}
                      {workout.template_id && !workout.program_workout_id && (
                        <Badge bg="info">
                          <i className="fas fa-copy me-1"></i>
                          Template
                        </Badge>
                      )}
                      {!workout.template_id && !workout.program_workout_id && (
                        <Badge bg="secondary">
                          <i className="fas fa-pencil-alt me-1"></i>
                          Manual
                        </Badge>
                      )}
                    </td>
                    <td>{formatDuration(workout.duration_minutes)}</td>
                    <td className="text-center">{workout.exercise_count || 0}</td>
                    <td className="text-end">
                      <div className="btn-group btn-group-sm">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleViewDetails(workout)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => handleEdit(workout)}
                          title="Edit"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDeleteConfirm(workout)}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card>
      )}

      {/* View Details Modal */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedWorkout?.name || 'Workout Details'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedWorkout && (
            <>
              <div className="mb-3">
                <div><strong>Date:</strong> {formatDate(selectedWorkout.workout_date)}</div>
                {selectedWorkout.workout_time && (
                  <div><strong>Time:</strong> {formatTime(selectedWorkout.workout_time)}</div>
                )}
                <div><strong>Duration:</strong> {formatDuration(selectedWorkout.duration_minutes)}</div>
                {selectedWorkout.notes && (
                  <div><strong>Notes:</strong> {selectedWorkout.notes}</div>
                )}
              </div>

              <h6>Exercises</h6>
              <Table size="sm" striped>
                <tbody>
                  {selectedWorkout.exercises && selectedWorkout.exercises.map((exercise) =>
                    renderExerciseDetails(exercise)
                  )}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowViewModal(false);
              handleEdit(selectedWorkout);
            }}
          >
            <i className="fas fa-edit me-1"></i>
            Edit Workout
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm.show}
        onHide={() => !isDeleting && setShowDeleteConfirm({ show: false, workoutId: null, workoutName: '' })}
      >
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Warning:</strong> This action cannot be undone!
          </Alert>
          <p>
            Are you sure you want to permanently delete the workout{' '}
            <strong>"{showDeleteConfirm.workoutName}"</strong>?
          </p>
          <p className="text-muted small">
            This will remove all exercise data and may affect your personal records.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteConfirm({ show: false, workoutId: null, workoutName: '' })}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <i className="fas fa-trash me-1"></i>
                Delete Workout
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WorkoutHistory;
